import { IconLoader2 } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { statusBadgeClass, statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import { isPlaceholderEmail } from "@/app/booking-calendar/lib/attendeeContact"
import { openableUid } from "@/app/booking-calendar/lib/manualRecord"
import { formatInZone } from "@/app/booking-calendar/lib/siteTime"
import { PAGE_SIZES, type PageSize } from "../searchCriteria"
import type { BookingSearchPage, BookingSearchResult } from "../searchApi"

/**
 * Whether a result can be opened. The appointment dialog opens a booking by uid: the server's
 * `openUid`, which is the Cal uid or, for an appointment recorded without scheduling (AB#868), the
 * synthetic one; the Cal uid alone from a server that predates it. A request that never reached a
 * slot — screened, abandoned, expired, refused — has neither, and nothing to open.
 */
export function canOpen(row: Pick<BookingSearchResult, "calBookingUid" | "openUid">): boolean {
  return openableUid(row) !== null
}

/** Where the results stand: nothing searched yet, on its way, failed, or answered. */
export type SearchResultsState = "idle" | "loading" | "error" | "ready"

export interface SearchResultsProps {
  state: SearchResultsState
  data?: BookingSearchPage
  /** What to say when the search failed. */
  errorMessage?: string
  /** A later page or a refresh is on its way while the current one is still shown. */
  isFetching?: boolean
  page: number
  pageSize: PageSize
  onPageChange: (page: number) => void
  onPageSizeChange: (size: PageSize) => void
  /** Offered on a failure; left out when the search cannot simply be sent again (a search by SSN). */
  onRetry?: () => void
  onOpen: (row: BookingSearchResult) => void
  /**
   * The results are one page of at most this many, not paged: a search by Social Security number,
   * which would have to send the number again to fetch another page.
   */
  singlePageOf?: number
}

/** The search's results: a paged table, or what stands in for one while there is nothing to show. */
export function SearchResults({
  state,
  data,
  errorMessage = "The search could not be completed.",
  isFetching = false,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRetry,
  onOpen,
  singlePageOf,
}: SearchResultsProps) {
  if (state === "idle") {
    return (
      <div className="rounded-xl border bg-card p-6 text-muted-foreground">
        Enter what you know about the appointment and choose Search.
      </div>
    )
  }

  if (state === "loading") {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Searching">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (state === "error") {
    return (
      <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
        <p className="text-sm text-destructive" role="alert">{errorMessage}</p>
        {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>}
      </div>
    )
  }

  const rows = data?.items ?? []
  const total = data?.totalCount ?? 0

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-6 text-muted-foreground" role="status">
        No appointments match the search.
      </div>
    )
  }

  if (singlePageOf !== undefined) {
    return (
      <>
        <ResultsTable rows={rows} isFetching={isFetching} onOpen={onOpen} />
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {total > rows.length
            ? `The first ${rows.length} of ${total} matches. Narrow the date range or add a detail to see the rest.`
            : `${total} ${total === 1 ? "match" : "matches"}.`}
        </p>
      </>
    )
  }

  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1)
  const first = page * pageSize + 1
  const last = Math.min(total, page * pageSize + rows.length)

  return (
    <>
      <ResultsTable rows={rows} isFetching={isFetching} onOpen={onOpen} />

      <nav className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground" aria-label="Search results pages">
        <span aria-live="polite">
          {first}–{last} of {total}
          {isFetching && <IconLoader2 className="ml-2 inline size-4 animate-spin" aria-hidden="true" />}
        </span>
        <div className="flex items-center gap-2">
          <Label htmlFor="search-page-size" className="text-xs">Rows per page</Label>
          <Select value={String(pageSize)} onValueChange={value => onPageSizeChange(Number(value) as PageSize)}>
            <SelectTrigger id="search-page-size" size="sm" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map(size => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => onPageChange(page + 1)}>Next</Button>
        </div>
      </nav>
    </>
  )
}

function ResultsTable({ rows, isFetching, onOpen }: { rows: BookingSearchResult[]; isFetching: boolean; onOpen: (row: BookingSearchResult) => void }) {
  return (
    <div className="rounded-xl border bg-card" aria-busy={isFetching || undefined}>
      <Table aria-label="Search results">
        <TableHeader>
          <TableRow>
            <TableHead>Confirmation number</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Start</TableHead>
            <TableHead>Site</TableHead>
            <TableHead>Host</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <SearchResultRow key={row.requestId} row={row} onOpen={onOpen} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function SearchResultRow({ row, onOpen }: { row: BookingSearchResult; onOpen: (row: BookingSearchResult) => void }) {
  const openable = canOpen(row)
  const email = row.email?.trim()
  const contact = [row.phoneNumber?.trim(), email && !isPlaceholderEmail(email) ? email : undefined].filter(Boolean).join(" · ")
  // The number the customer was told to keep (AB#817). A request that never reached a slot, or one not
  // yet backfilled, has none: it shows a dash, and its button is named by the reference code instead.
  const confirmationCode = row.confirmationCode?.trim() || null

  return (
    <TableRow
      data-openable={openable}
      aria-disabled={openable ? undefined : true}
      className={openable ? "cursor-pointer" : "text-muted-foreground"}
      // The confirmation number is the row's button for keyboards and screen readers; the rest of the
      // row is a larger target for a mouse, as on the incomplete-bookings list.
      onClick={openable ? () => onOpen(row) : undefined}
    >
      <TableCell>
        {openable ? (
          <Button
            variant="link"
            className="h-auto p-0 font-mono"
            onClick={event => { event.stopPropagation(); onOpen(row) }}
            aria-label={`Open appointment ${confirmationCode ?? row.referenceCode}`}
          >
            {confirmationCode ?? "—"}
          </Button>
        ) : (
          <div className="flex flex-col">
            <span className="font-mono">{confirmationCode ?? "—"}</span>
            <span className="text-xs">Not in the scheduling system</span>
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="font-medium">{row.customerName || "—"}</div>
        {contact && <div className="text-sm text-muted-foreground">{contact}</div>}
      </TableCell>
      <TableCell>{formatInZone(row.start)}</TableCell>
      <TableCell>{row.siteName ?? row.calTeamSlug}</TableCell>
      <TableCell>{row.hostName ?? <span className="text-muted-foreground">Not yet known</span>}</TableCell>
      <TableCell>
        <Badge variant="outline" className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</Badge>
      </TableCell>
    </TableRow>
  )
}
